import { AttachmentButtonImage } from "@rentiohq/shared-frontend/dist/components/components/ChatSendBar/ChatSendBar.styles";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import {
  EGroupActionType,
  IGroupActionContentBase,
  TGroupActionsLocale,
} from "@rentiohq/shared-frontend/dist/types/groupAction.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Labelled, TextField } from "@rentiohq/web-shared/dist/components";
import { showDropErrors } from "@rentiohq/web-shared/dist/utils/file";
import {
  attachmentToBase64,
  canPreviewAttachment,
  getAttachmentIcon,
} from "@rentiohq/web-shared-next/dist/utils/attachment";
import { isEmpty } from "lodash";
import React from "react";
import { DropEvent, FileRejection, useDropzone } from "react-dropzone";
import { getLocaleKey } from "../GroupActions.util";
import {
  AttachmentButton,
  AttachmentCloseIcon,
  AttachmentSection,
  AttachmentText,
  AttachmentWrap,
  AttachmentImage,
  AttachmentRow,
} from "./AddGroupActionContentWithTextfieldSection.styled";

const ACCEPTED_FILE_TYPES =
  "image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf";

export interface IAttachmentData {
  file: File;
  preview?: string;
}

export interface IAddGroupActionContentWithTextfieldSectionProps {
  type: EGroupActionType;
  index: number;
  locale: TGroupActionsLocale;
  initialContent: IGroupActionContentBase;
  children?: React.ReactNode;
  onChange: (content: IGroupActionContentBase) => void;
  onChangeTitle?: (key: TGroupActionsLocale, value?: string) => void;
  onChangeSubject?: (key: TGroupActionsLocale, value?: string) => void;
  onChangeError?: (error?: string) => void;
  onChangeAttachment?: (
    locale: TGroupActionsLocale,
    attachment?: IAttachmentData,
  ) => void;
}

const AddGroupActionContentWithTextfieldSection = (
  props: IAddGroupActionContentWithTextfieldSectionProps,
) => {
  const {
    type,
    index,
    locale,
    initialContent,
    onChange,
    onChangeTitle,
    onChangeSubject,
    onChangeError,
    onChangeAttachment,
  } = props;

  // State
  const [content, setContent] =
    React.useState<IGroupActionContentBase>(initialContent);
  const [title, setTitle] = React.useState<string>("");
  const [titleError, setTitleError] = React.useState<string | undefined>("");

  const [subject, setSubject] = React.useState<string>("");
  const [subjectError, setSubjectError] = React.useState<string | undefined>(
    "",
  );

  const [contentError, setContentError] = React.useState<string | undefined>(
    "",
  );

  // Attachment state
  const [attachment, setAttachment] = React.useState<any>(null);
  const [attachmentPreview, setAttachmentPreview] = React.useState<
    string | undefined
  >();
  const [attachmentError, setAttachmentError] = React.useState<
    string | undefined
  >();

  // Effects
  React.useEffect(() => {
    onChange(content);
  }, [content]);

  React.useEffect(() => {
    onChangeTitle?.(locale, title);
  }, [title]);

  React.useEffect(() => {
    onChangeSubject?.(locale, subject);
  }, [subject]);

  React.useEffect(() => {
    onChangeError?.(
      titleError || subjectError || contentError || attachmentError,
    );
  }, [titleError, subjectError, contentError, attachmentError]);

  // Notify parent when attachment changes
  React.useEffect(() => {
    if (attachment?.file) {
      onChangeAttachment?.(locale, {
        file: attachment.file,
        preview: attachmentPreview,
      });
    } else {
      onChangeAttachment?.(locale, undefined);
    }
  }, [attachment, attachmentPreview]);

  // Helpers
  const getMaxTextLength = () => {
    if (type === EGroupActionType.Sms) {
      return 160;
    }

    if (type === EGroupActionType.Notification) {
      return 178;
    }

    if ([EGroupActionType.Chat, EGroupActionType.Document].includes(type)) {
      return CONFIG.CHAT_AND_EMAIL_TEXT_LIMIT;
    }
  };

  // Events
  const handleContentChange = (value: string) => {
    setContent({ ...content, value });
  };

  // Attachment handling for Chat type
  const handleDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
    event: DropEvent,
  ) => {
    showDropErrors({ acceptedFiles, fileRejections, event });

    if (acceptedFiles.length === 0) {
      if (fileRejections.length > 0) {
        setAttachmentError(
          getLocalizedText("group_actions.chat.attachment.error.invalid_type"),
        );
      }
      return;
    }

    const file = acceptedFiles[0];
    setAttachmentError(undefined);

    try {
      const attachmentInfo = {
        type: file.type,
        path: URL.createObjectURL(file),
        filename: file.name,
      };
      setAttachment({ ...attachmentInfo, file });

      if (canPreviewAttachment(attachmentInfo)) {
        const base64 = await attachmentToBase64(file);
        setAttachmentPreview(base64 as string);
      } else {
        setAttachmentPreview(undefined);
      }
    } catch (error) {
      setAttachmentError(
        getLocalizedText("group_actions.chat.attachment.error.upload_failed"),
      );
    }
  };

  const handleRemoveAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(undefined);
    setAttachmentError(undefined);
  };

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    maxSize: CONFIG.UPLOAD_MAX_FILE_SIZE,
    accept: ACCEPTED_FILE_TYPES,
    noClick: true,
    noKeyboard: true,
  });

  // Renders
  const renderChildren = () => {
    const lowerCaseType = type.toLowerCase();

    switch (type) {
      case EGroupActionType.Notification:
        return (
          <>
            <TextField
              name={`group_actions_modal_${lowerCaseType}_title_${locale}`}
              label={getLocalizedText(
                `group_actions.modal.title.label.${getLocaleKey(locale)}`,
              )}
              value={title}
              error={titleError}
              type="input"
              onChange={(
                e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>,
              ) => {
                const { value } = e.target;

                if (isEmpty(value)) {
                  setTitleError(
                    `The ${getLocalizedText(
                      `group_actions.modal.title.label.${getLocaleKey(locale)}`,
                    )} is required`,
                  );
                } else {
                  setTitleError(undefined);
                }

                setTitle(value);
              }}
            />
            <Spacer weight={ESpacerWeight.W08} />
          </>
        );

      case EGroupActionType.Email:
        return (
          <>
            <TextField
              name={`group_actions_modal_${lowerCaseType}_subject_${locale}`}
              label={getLocalizedText(
                `group_actions.modal.subject.label.${getLocaleKey(locale)}`,
              )}
              value={subject}
              error={subjectError}
              type="input"
              onChange={(
                e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>,
              ) => {
                const { value } = e.target;

                if (isEmpty(value)) {
                  setSubjectError(
                    `The ${getLocalizedText(
                      `group_actions.modal.subject.label.${getLocaleKey(
                        locale,
                      )}`,
                    )} is required`,
                  );
                } else {
                  setSubjectError(undefined);
                }

                setSubject(value);
              }}
            />
            <Spacer weight={ESpacerWeight.W08} />
          </>
        );

      default:
        return null;
    }
  };

  // Render attachment UI for Chat type (matching ChatSendBar style)
  const renderAttachmentSection = () => (
    <AttachmentSection isDragActive={isDragActive} {...getRootProps()}>
      <input {...getInputProps()} />
      <Spacer weight={ESpacerWeight.W08} />
      <Labelled
        label={getLocalizedText("group_actions.chat.attachment.label")}
        error={attachmentError}
        optional
      >
        <AttachmentRow>
          {attachment ? (
            <AttachmentWrap onClick={handleRemoveAttachment}>
              {attachmentPreview ? (
                <AttachmentImage
                  src={attachmentPreview}
                  alt={attachment.filename}
                />
              ) : (
                <AttachmentText>{getAttachmentIcon(attachment)}</AttachmentText>
              )}
              <AttachmentCloseIcon
                type="button"
                title={getLocalizedText("group_actions.chat.attachment.remove")}
              >
                ✕
              </AttachmentCloseIcon>
            </AttachmentWrap>
          ) : (
            <AttachmentButton
              type="button"
              onClick={open}
              title={getLocalizedText("group_actions.chat.attachment.add")}
            >
              <AttachmentButtonImage />
            </AttachmentButton>
          )}
        </AttachmentRow>
      </Labelled>
    </AttachmentSection>
  );

  return (
    <>
      {index > 0 && <Spacer weight={ESpacerWeight.W32} />}
      {renderChildren()}
      <TextField
        name={content.key}
        label={content.label}
        value={content.value || ""}
        type="textarea"
        maxLength={getMaxTextLength()}
        multiline
        error={contentError}
        helpText={
          [EGroupActionType.Chat, EGroupActionType.Document].includes(type)
            ? getLocalizedText("system.form.error.text.max_length", {
                value: `${CONFIG.CHAT_AND_EMAIL_TEXT_LIMIT}`,
              })
            : undefined
        }
        onChange={(
          e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>,
        ) => {
          const { value } = e.target;
          if (isEmpty(value)) {
            setContentError(`The ${content.label} is required`);
          } else {
            setContentError(undefined);
          }

          handleContentChange(value);
        }}
      />
      {type === EGroupActionType.Chat && renderAttachmentSection()}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default AddGroupActionContentWithTextfieldSection;
